import type { ReactElement, ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { FeatureFlagsResponse } from '@shared/api';
import { getFeatures } from '../api/client';

/**
 * Build Engine Selector v1 is shelved: Studio and Forge stay fully invisible
 * (no picker, no rebuild menu, no persisted-state rendering) whenever
 * `experimentalEngines` is false, regardless of what `proposalStudio` /
 * `proposalForge` individually say — those only matter (to tell the two
 * experiments apart) once the master flag is true. Every Studio/Forge-
 * adjacent surface should gate on `experimentalEngines` first.
 */
export type FeatureFlags = FeatureFlagsResponse['features'];

const DEFAULT_FLAGS: FeatureFlags = {
  experimentalEngines: false,
  proposalStudio: false,
  proposalForge: false,
};

export const FeatureFlagsContext = createContext<FeatureFlags>(DEFAULT_FLAGS);

export function useFeatureFlags(): FeatureFlags {
  return useContext(FeatureFlagsContext);
}

/** Convenience for the common case: the ONE flag every Studio/Forge surface gates on. */
export function useExperimentalEngines(): boolean {
  return useContext(FeatureFlagsContext).experimentalEngines;
}

/**
 * Fetches `GET /api/features` once per app load. A failed or missing fetch
 * just leaves every flag at its off default — a feature-flag lookup failing
 * must never surface an experimental surface.
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }): ReactElement {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);

  useEffect(() => {
    let live = true;
    void getFeatures()
      .then((res) => {
        if (live) setFlags(res.features);
      })
      .catch(() => {
        // Stay on the all-off default.
      });
    return () => {
      live = false;
    };
  }, []);

  const value = useMemo(() => flags, [flags]);

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}
